import { toast } from "sonner";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import CustomSelect from "../../components/CustomSelect";
import PageHeader from "../../components/PageHeader";
import SubHeadings from "../../components/SubHeadings";
import { useEffect, useState } from "react";
import FileUpload from "../../components/common/FileUpload";
import admissionApis from "../../api/admissionApis";
import { CreateAdmissionPayload } from "../../types/admissionType";
import { useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { fetchClasses } from "../../features/classes/ClassesSlice";
import { useDispatch } from "react-redux";
import { useBranchCode } from "../../util/hooks/useBranch";
import AcademicCycleSelect from "../../components/common-input/AcademicCycle";
import { removeNonNumeric } from "../../util/functions/UniversalFunctions";

export default function AddNewAdmissions() {
  const initialFormState: CreateAdmissionPayload = {
    studentName: "",
    gender: "",
    dateOfBirth: "",
    admissionYear: "",
    academicCycleId: 0,
    motherName: "",
    motherPhone: "",
    motherEmail: "",
    fatherName: "",
    fatherPhone: "",
    fatherEmail: "",
    studentPhoto: "",
    branchCode: "",
    studentCategory: "",
  };

  const branchCode = useBranchCode();

  const [formData, setFormData] =
    useState<CreateAdmissionPayload>(initialFormState);

  const dispatch = useDispatch<AppDispatch>();
  // const { classes, filters } = useSelector((state: RootState) => state.classes);

  // useEffect(() => {
  //   dispatch(fetchClasses({ page: 0, size: 10, filters }));
  // }, [dispatch, filters]);

  const handleInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = event.target;

    // phone number fields
    if (name === "motherPhoneNumber" || name === "fatherPhoneNumber") {
      const numericValue = removeNonNumeric(value).slice(0, 10); // max 10 digits

      setFormData((prev) => ({
        ...prev,
        [name]: numericValue,
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateParentInfo = () => {
    const hasMother = formData.motherName || formData.motherPhone;
    // || formData.motherEmail;
    const hasFather = formData.fatherName || formData.fatherPhone;
    // || formData.fatherEmail;

    return hasMother || hasFather;
  };

  const buildPostPayload = (): CreateAdmissionPayload => {
    return {
      ...formData,
      branchCode: branchCode,
      academicCycleId: Number(formData.academicCycleId),
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.studentName ||
      !formData.dateOfBirth ||
      !formData.gender ||
      !formData.admissionYear ||
      !formData.academicCycleId
    ) {
      toast.error("Please fill all required student details");
      return;
    }

    if (!validateParentInfo()) {
      toast.error("Any one parent detail is mandatory");
      return;
    }

    const payload = buildPostPayload();

    const response = await admissionApis.createAdmission(payload);

    if (response?.status === 1) {
      setFormData(initialFormState);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex justify-between">
        <PageHeader title="ADD ADMISSION" showBack />
      </div>

      <form className="grid gap-5" onSubmit={handleSubmit}>
        <div className="bg-white px-6 py-5 rounded-lg flex flex-col gap-6">
          <SubHeadings text="Student Details" />
          <div className="flex flex-col-reverse lg:flex-row gap-5 lg:gap-6">
            <div className="grid gap-6 w-full">
              <div className="grid lg:grid-cols-2 gap-7">
                <CustomInput
                  name="studentName"
                  label="Name"
                  type="text"
                  placeholder="Enter the student's name"
                  value={formData.studentName}
                  onChange={handleInputChange}
                  inputClassName="w-full"
                  required
                />
                <CustomInput
                  name="dateOfBirth"
                  label="Date of Birth"
                  type="date"
                  placeholder="DD/MM/YYYY"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                  inputClassName="w-full"
                  required
                />
              </div>

              <div className="flex items-center gap-7">
                <label className="text-sm font-semibold text-neutral-400 block">
                  Gender
                  <span className="text-error-500">*</span>
                </label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <CustomInput
                      name="gender"
                      type="radio"
                      value="MALE"
                      checked={formData?.gender === "MALE"}
                      onChange={handleInputChange}
                      inputClassName="w-3 h-3"
                    />
                    <span
                      className={`text-sm font-semibold ${
                        formData?.gender === "MALE"
                          ? "text-primary-500"
                          : "text-neutral-300"
                      }`}
                    >
                      Male
                    </span>
                  </label>

                  <label className="flex items-center gap-2">
                    <CustomInput
                      name="gender"
                      type="radio"
                      value="FEMALE"
                      checked={formData?.gender === "FEMALE"}
                      onChange={handleInputChange}
                      inputClassName="w-3 h-3"
                    />
                    <span
                      className={`text-sm font-semibold ${
                        formData.gender === "FEMALE"
                          ? "text-primary-500"
                          : "text-neutral-300"
                      }`}
                    >
                      Female
                    </span>
                  </label>
                </div>
              </div>

              <div className="grid lg:grid-cols-3 gap-7">
                <CustomSelect
                  name="admissionYear"
                  label="Admission Year"
                  required
                  value={formData.admissionYear}
                  options={["2026-27", "2027–28", "2028–29"]}
                  placeholder="Select admission year"
                  onChange={handleInputChange}
                />
                <AcademicCycleSelect
                  name="academicCycleId"
                  label="Enquired for Academic Cycle"
                  required
                  value={formData.academicCycleId}
                  placeholder="Select academic cycle"
                  onChange={handleInputChange}
                />
                <CustomSelect
                  name="studentCategory"
                  label="Student Category"
                  value={formData.studentCategory}
                  options={[
                    { label: "General", value: "GENERAL" },
                    { label: "OBC", value: "OBC" },
                    { label: "SC", value: "SC" },
                    { label: "ST", value: "ST" },
                  ]}
                  placeholder="Select student category"
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
            <div className="max-w-md">
              <FileUpload
                value={formData.studentPhoto}
                onUploadComplete={(url) =>
                  setFormData((prev) => ({ ...prev, studentPhoto: url }))
                }
                className="md:w-52 w-36 aspect-square"
              />
            </div>
          </div>
        </div>

        <div className="bg-white px-6 py-5 rounded-lg flex flex-col gap-6">
          <div className="flex justify-between items-center w-full">
            <SubHeadings text="Parent Details" />
            <span className="text-xs text-error-500 font-medium">
              *Any one parent detail is mandatory
            </span>
          </div>

          <div className="grid lg:grid-cols-2 gap-7">
            <CustomInput
              name="motherName"
              label="Mother's Name"
              type="text"
              placeholder="Enter mother's name"
              value={formData.motherName}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
            <CustomInput
              name="motherPhone"
              label="Mother's Phone Number"
              type="tel"
              placeholder="Enter mother's phone number"
              value={formData.motherPhone}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
            <CustomInput
              name="motherEmail"
              label="Mother's Email"
              type="email"
              placeholder="Enter mother's email"
              value={formData.motherEmail}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
            <CustomInput
              name="fatherName"
              label="Father's Name"
              type="text"
              placeholder="Enter father's name"
              value={formData.fatherName}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
            <CustomInput
              name="fatherPhone"
              label="Father's Phone Number"
              type="tel"
              placeholder="Enter father's phone number"
              value={formData.fatherPhone}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
            <CustomInput
              name="fatherEmail"
              label="Father's Email"
              type="email"
              placeholder="Enter father's email"
              value={formData.fatherEmail}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
          </div>
        </div>

        <div className="flex justify-end items-end mx-6">
          <CustomButton color="bg-primary-500" text="Submit" type="submit" />
        </div>
      </form>
    </div>
  );
}
